import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import EventCard from '@/components/EventCard';
import CountdownTimer from '@/components/CountdownTimer';
import EventPoster from '@/components/EventPoster';
import { getEvents } from '@/lib/api';
import { formatDate, formatTime, formatPrice } from '@/lib/utils';

// Page config per category
const PAGE_CONFIG = {
  'Revival Night': {
    title: 'Revival Night',
    subtitle: 'Experience the fire of revival',
    description: 'Join Prophet Malluc for a powerful Revival Night of healing, deliverance, and the prophetic. Experience the move of God like never before.',
    heroImage: 'https://customer-assets.emergentagent.com/job_prophecy-live/artifacts/uwjs6y6c_Screenshot%202026-02-15%20at%2011.45.34%E2%80%AFAM.png',
    ctaText: 'Get Tickets',
    comingSoon: false,
  },
  'Prophetic School': {
    title: 'Prophetic School',
    subtitle: 'Develop your prophetic calling',
    description: 'Intensive training for those called to the prophetic ministry. Learn to hear the voice of God, operate in the gifts of the Spirit, and develop your calling under the guidance of Prophet Malluc.',
    heroImage: 'https://images.unsplash.com/photo-1650065045891-e3a7c96a4ad7?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
    ctaText: 'Enroll Now',
    comingSoon: true,
  },
  'Mentorship': {
    title: 'Mentorship',
    subtitle: 'One-on-one spiritual guidance',
    description: 'Personalized mentorship sessions with Prophet Malluc. Receive prophetic guidance, spiritual direction, and develop a deeper walk with God through one-on-one sessions.',
    heroImage: 'https://images.unsplash.com/photo-1715620885787-999c8d792458?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85',
    ctaText: 'Book a Session',
    comingSoon: true,
  },
};

export default function CategoryPage({ category }) {
  const config = PAGE_CONFIG[category] || PAGE_CONFIG['Revival Night'];
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (config.comingSoon) {
      setLoading(false);
      return;
    }
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const res = await getEvents(category);
        setEvents(res.data.events || []);
      } catch (e) {
        console.error('Failed to fetch events:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [category, config.comingSoon]);

  const now = new Date();
  const upcomingEvents = events
    .filter(e => new Date(e.start_at) > now)
    .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
  const nextEvent = upcomingEvents[0];

  // Coming Soon page
  if (config.comingSoon) {
    return (
      <div className="bg-white min-h-screen">
        <section className="pt-20 sm:pt-28 pb-20 sm:pb-32">
          <div className="mx-auto w-full max-w-3xl px-6 sm:px-10 text-center">
            <p className="text-[11px] uppercase tracking-[0.2em] text-gray-400 font-body font-medium">
              Prophet Malluc Ministries
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-gray-900 mt-3">
              {config.title}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-gray-500 font-body max-w-xl mx-auto leading-relaxed">
              {config.description}
            </p>

            {/* Poster-style image */}
            <div className="mt-10 mx-auto max-w-2xl">
              <div className="bg-[#f5f0e8] p-6 sm:p-8">
                <h2
                  className="text-center font-body font-light text-2xl sm:text-3xl lg:text-4xl text-gray-900 mb-6 sm:mb-8"
                  style={{ letterSpacing: '0.35em' }}
                >
                  {config.title.toUpperCase()}
                </h2>
                <div className="relative overflow-hidden aspect-[16/10]">
                  <img
                    src={config.heroImage}
                    alt={config.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-end justify-center pb-6">
                    <span
                      className="text-white font-body font-light text-3xl sm:text-4xl lg:text-5xl"
                      style={{ letterSpacing: '0.15em', textShadow: '0 2px 20px rgba(0,0,0,0.5)' }}
                    >
                      COMING SOON
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Coming Soon text */}
            <div className="mt-12">
              <h2 className="font-heading text-3xl sm:text-4xl font-medium text-gray-900 italic">
                Stay Tuned
              </h2>
              <p className="mt-4 text-base text-gray-500 font-body max-w-md mx-auto leading-relaxed">
                We are preparing something special for you. Details will be announced soon.
              </p>
              <p className="mt-2 text-sm text-gray-400 font-body">
                Follow us for updates.
              </p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Active category page with events
  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="pt-16 sm:pt-20 pb-10">
        <div className="mx-auto w-full max-w-4xl px-6 sm:px-10 text-center">
          <p className="text-[11px] uppercase tracking-[0.2em] text-gray-400 font-body font-medium">
            Prophet Malluc Ministries
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-gray-900 mt-3">
            {config.title}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-gray-500 font-body max-w-xl mx-auto leading-relaxed">
            {config.description}
          </p>

          {/* Next Event Hero */}
          {nextEvent && (
            <>
              <div className="mt-10 mx-auto max-w-2xl">
                <Link to={`/events/${nextEvent.id}`}>
                  <div className="relative overflow-hidden group">
                    <EventPoster eventTitle={nextEvent.title} />
                  </div>
                </Link>
              </div>

              <div className="mt-10">
                <p className="text-[11px] uppercase tracking-[0.2em] text-gray-400 font-body font-medium mb-2">
                  Next {config.title}
                </p>
                <h2 className="font-heading text-2xl sm:text-3xl font-medium text-gray-900">
                  {nextEvent.title}
                </h2>
                {nextEvent.location && nextEvent.location !== 'To be announced' ? (
                  <>
                    <div className="mt-3 text-sm text-gray-400 font-body">
                      {formatDate(nextEvent.start_at)} &middot; {formatTime(nextEvent.start_at)} &middot; {nextEvent.location?.split(',')[0]}
                    </div>
                    <div className="mt-6">
                      <Link to={`/events/${nextEvent.id}`}>
                        <Button
                          className="h-12 px-8 rounded-sm bg-[#1a2e4a] text-white font-body text-sm font-medium hover:bg-[#0f1b2d] transition-colors tracking-wider uppercase"
                        >
                          {config.ctaText} &mdash; {formatPrice(nextEvent.price, nextEvent.currency)}
                        </Button>
                      </Link>
                    </div>
                  </>
                ) : (
                  <div className="mt-4">
                    <p className="text-sm text-gray-400 font-body italic">Coming Soon — Details to be announced</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Divider */}
      <div className="mx-auto w-full max-w-7xl px-6 sm:px-10 lg:px-16">
        <div className="border-t border-gray-100" />
      </div>

      {/* All Events */}
      {events.length > 1 && (
        <section className="py-16 sm:py-24">
          <div className="mx-auto w-full max-w-7xl px-6 sm:px-10 lg:px-16">
            <div className="text-center mb-12">
              <p className="text-[11px] uppercase tracking-[0.2em] text-gray-400 font-body font-medium mb-3">
                All
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl font-medium text-gray-900">
                {config.title} Events
              </h2>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                {[1, 2, 3].map(i => (
                  <div key={i} className="animate-pulse">
                    <div className="aspect-[4/3] bg-gray-100" />
                    <div className="pt-5 space-y-3">
                      <div className="h-3 bg-gray-100 rounded w-1/4" />
                      <div className="h-5 bg-gray-100 rounded w-3/4" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                {events.map(event => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
